import type GUI from 'lil-gui';
import type { Object3D } from 'three/webgpu';

export interface ModelTransformOptions {
  /** Slider ranges; defaults: pos ±10, rot ±π, scale 0.01–10 */
  readonly ranges?: {
    readonly position?: number;
    readonly rotation?: number;
    readonly scaleMin?: number;
    readonly scaleMax?: number;
  };
  /**
   * Custom export handler. If omitted, defaults to console.info with the
   * registry-paste-ready snippet. Receives the snippet text directly.
   */
  readonly onExport?: (snippet: string) => void;
}

export interface ModelTransformHandle {
  /** Re-syncs slider values from the underlying Object3D (useful after external transform changes) */
  refresh(): void;
  destroy(): void;
}

interface TransformState {
  posX: number;
  posY: number;
  posZ: number;
  rotX: number;
  rotY: number;
  rotZ: number;
  scale: number;
  uniformScale: boolean;
  scaleX: number;
  scaleY: number;
  scaleZ: number;
}

/**
 * Installs a transform-control folder on `panel` that drives `target.position`,
 * `target.rotation`, and `target.scale`. Used by both the standalone inspector
 * route AND any in-scene placement so values authored in dev land in the
 * registry without conversion drift.
 */
export function installModelTransformFolder(
  panel: GUI,
  target: Object3D,
  options: ModelTransformOptions = {},
): ModelTransformHandle {
  const ranges = {
    position: options.ranges?.position ?? 10,
    rotation: options.ranges?.rotation ?? Math.PI,
    scaleMin: options.ranges?.scaleMin ?? 0.01,
    scaleMax: options.ranges?.scaleMax ?? 10,
  };

  const initialUniform = approxEqual(target.scale.x, target.scale.y) && approxEqual(target.scale.y, target.scale.z);

  const state: TransformState = {
    posX: target.position.x,
    posY: target.position.y,
    posZ: target.position.z,
    rotX: target.rotation.x,
    rotY: target.rotation.y,
    rotZ: target.rotation.z,
    scale: initialUniform ? target.scale.x : 1,
    uniformScale: initialUniform,
    scaleX: target.scale.x,
    scaleY: target.scale.y,
    scaleZ: target.scale.z,
  };

  const folder = panel.addFolder('Transform');

  const pos = folder.addFolder('Position');
  pos.add(state, 'posX', -ranges.position, ranges.position, 0.01).name('X')
    .onChange((v: number) => { target.position.x = v; });
  pos.add(state, 'posY', -ranges.position, ranges.position, 0.01).name('Y')
    .onChange((v: number) => { target.position.y = v; });
  pos.add(state, 'posZ', -ranges.position, ranges.position, 0.01).name('Z')
    .onChange((v: number) => { target.position.z = v; });

  const rot = folder.addFolder('Rotation (rad)');
  rot.add(state, 'rotX', -ranges.rotation, ranges.rotation, 0.01).name('X')
    .onChange((v: number) => { target.rotation.x = v; });
  rot.add(state, 'rotY', -ranges.rotation, ranges.rotation, 0.01).name('Y')
    .onChange((v: number) => { target.rotation.y = v; });
  rot.add(state, 'rotZ', -ranges.rotation, ranges.rotation, 0.01).name('Z')
    .onChange((v: number) => { target.rotation.z = v; });

  const scl = folder.addFolder('Scale');
  const uniformCtl = scl.add(state, 'uniformScale').name('Uniform');
  const scaleCtl = scl.add(state, 'scale', ranges.scaleMin, ranges.scaleMax, 0.005).name('Uniform scale')
    .onChange((v: number) => {
      if (state.uniformScale) target.scale.set(v, v, v);
    });
  const scaleXCtl = scl.add(state, 'scaleX', ranges.scaleMin, ranges.scaleMax, 0.005).name('X')
    .onChange((v: number) => { if (!state.uniformScale) target.scale.x = v; });
  const scaleYCtl = scl.add(state, 'scaleY', ranges.scaleMin, ranges.scaleMax, 0.005).name('Y')
    .onChange((v: number) => { if (!state.uniformScale) target.scale.y = v; });
  const scaleZCtl = scl.add(state, 'scaleZ', ranges.scaleMin, ranges.scaleMax, 0.005).name('Z')
    .onChange((v: number) => { if (!state.uniformScale) target.scale.z = v; });

  const updateScaleVisibility = (): void => {
    scaleCtl.show(state.uniformScale);
    scaleXCtl.show(!state.uniformScale);
    scaleYCtl.show(!state.uniformScale);
    scaleZCtl.show(!state.uniformScale);
  };
  updateScaleVisibility();
  uniformCtl.onChange(() => updateScaleVisibility());

  folder.add(
    {
      export: (): void => {
        const snippet = formatSnippet(state);
        if (options.onExport) {
          options.onExport(snippet);
        } else {
          // eslint-disable-next-line no-console
          console.info('[modelControls] paste into models.ts:\n' + snippet);
        }
      },
    },
    'export',
  ).name('⎘ Export defaults');

  pos.close();
  rot.close();
  scl.close();
  folder.close();

  return {
    refresh: () => {
      state.posX = target.position.x;
      state.posY = target.position.y;
      state.posZ = target.position.z;
      state.rotX = target.rotation.x;
      state.rotY = target.rotation.y;
      state.rotZ = target.rotation.z;
      state.scaleX = target.scale.x;
      state.scaleY = target.scale.y;
      state.scaleZ = target.scale.z;
      const uniform = approxEqual(target.scale.x, target.scale.y) && approxEqual(target.scale.y, target.scale.z);
      state.uniformScale = uniform;
      if (uniform) state.scale = target.scale.x;
      folder.controllersRecursive().forEach(c => c.updateDisplay());
    },
    destroy: () => folder.destroy(),
  };
}

function formatSnippet(s: TransformState): string {
  const px = round(s.posX);
  const py = round(s.posY);
  const pz = round(s.posZ);
  const rx = round(s.rotX);
  const ry = round(s.rotY);
  const rz = round(s.rotZ);
  let scaleLine: string;
  if (s.uniformScale) {
    scaleLine = `    scale: ${round(s.scale)},`;
  } else {
    scaleLine = `    scale: [${round(s.scaleX)}, ${round(s.scaleY)}, ${round(s.scaleZ)}],`;
  }
  return [
    '  defaults: {',
    `    position: [${px}, ${py}, ${pz}],`,
    `    rotation: [${rx}, ${ry}, ${rz}],`,
    scaleLine,
    '  },',
  ].join('\n');
}

function round(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function approxEqual(a: number, b: number): boolean {
  return Math.abs(a - b) < 1e-6;
}
